import React, {Component} from 'react';
import {connect} from 'react-redux';
import {findDOMNode} from 'react-dom';
import memoize from 'memoize-one';
import moment from 'moment-timezone';
import parse from 'html-react-parser';

import {Button} from 'primereact/button';
import {
    Dropdown,
    DropdownChangeEvent
} from 'primereact/dropdown';
import {
    InputNumber,
    InputNumberProps
} from 'primereact/inputnumber';
import {InputText} from 'primereact/inputtext';
import {MultiSelect} from 'primereact/multiselect';
import {OverlayPanel} from 'primereact/overlaypanel';

import DateField from '~/frontend/src/components/base/shared/field/valueTypeFields/DateField';
import TimeField from '~/frontend/src/components/base/shared/field/valueTypeFields/TimeField';
import TimestampField from '~/frontend/src/components/base/shared/field/valueTypeFields/TimestampField';

import User from '~/shared/src/model/User';
import {__} from '~/shared/src/utilities/messageCatalog';
import metaModel from '~/shared/src/utilities/metaModel';
import {
    FilterMatchMode,
    FilterValueType
} from '~/shared/src/validationSchemes/modules/overview';
import {FilterConfigSet} from '~/shared/src/utilities/filterConfig';
import {
    CustomFilter,
    CustomFilterMode,
    getCustomFilterMode,
    getCustomFilters,
    getDefaultFilterMatchMode,
    getValue,
    getValueStringFromMatchModeAndValue
} from '~/shared/src/utilities/filterUtils';
import {
    isIntlNumberFormatCurrency,
    isIntlNumberFormatUnit
} from '~/shared/src/utilities/valueTesters';


import {StoreState} from '~/frontend/src/store';
import {
    boolRenderer,
    directionRenderer,
    statusColoringRenderer,
    statusRenderer
} from '~/frontend/src/utilities/valueRenderers';
import {getValueDisplayName} from '~/frontend/src/utilities/renderHelpers';

export type AlternativeValueField = {
    label : string;
    icon : string;
    active : boolean;
    getField : ( value : any, matchMode : FilterMatchMode, params : FilterConfigSet, disabled : boolean, onChange : ( value : any, matchMode? : FilterMatchMode ) => void, language : string ) => React.ReactNode;
};

type ValueObject = {
    id? : number;
    value : string;
    label : string;
    matchMode? : FilterMatchMode;
    sortOrder? : number;
};

export const valueTypeToFilterMatchModeDropdownOptionsMapping = {
           BOOLEAN             : [],
           COLOR               : [ 'startsWith', 'notStartsWith', 'contains', 'notContains', 'endsWith', 'notEndsWith', 'equals', 'notEquals', 'empty', 'notEmpty' ],
           COORDINATE          : [ 'startsWith', 'notStartsWith', 'contains', 'notContains', 'endsWith', 'notEndsWith', 'equals', 'notEquals', 'empty', 'notEmpty' ],
           CURRENCY            : [ 'equals', 'notEquals', 'lt', 'lte', 'gt', 'gte', 'empty', 'notEmpty' ],
           DATE                : [ 'equals', 'notEquals', 'lt', 'lte', 'gt', 'gte', 'empty', 'notEmpty' ],
           DOUBLE              : [ 'equals', 'notEquals', 'lt', 'lte', 'gt', 'gte', 'empty', 'notEmpty' ],
           DURATION            : [ 'equals', 'notEquals', 'lt', 'lte', 'gt', 'gte', 'empty', 'notEmpty' ],
           EMAIL               : [ 'startsWith', 'notStartsWith', 'contains', 'notContains', 'endsWith', 'notEndsWith', 'equals', 'notEquals', 'empty', 'notEmpty' ],
           IBAN                : [ 'startsWith', 'notStartsWith', 'contains', 'notContains', 'endsWith', 'notEndsWith', 'equals', 'notEquals', 'empty', 'notEmpty' ],
           IMAGEDATA           : [ 'startsWith', 'notStartsWith', 'contains', 'notContains', 'endsWith', 'notEndsWith', 'empty', 'notEmpty' ],
           INTEGER             : [ 'equals', 'notEquals', 'lt', 'lte', 'gt', 'gte', 'empty', 'notEmpty' ],
           JSON                : [ 'startsWith', 'notStartsWith', 'contains', 'notContains', 'endsWith', 'notEndsWith', 'equals', 'notEquals', 'empty', 'notEmpty' ],
           LINK                : [ 'startsWith', 'notStartsWith', 'contains', 'notContains', 'endsWith', 'notEndsWith', 'equals', 'notEquals', 'empty', 'notEmpty' ],
           PHONE               : [ 'startsWith', 'notStartsWith', 'contains', 'notContains', 'endsWith', 'notEndsWith', 'equals', 'notEquals', 'empty', 'notEmpty' ],
           REFDIRECTION        : [],
           REFTYPEWITHDIRECTION: [],
           STATUS              : [],
           STATUSID            : [],
           STATUSNAME          : [ 'startsWith', 'notStartsWith', 'contains', 'notContains', 'endsWith', 'notEndsWith', 'equals', 'notEquals', 'empty', 'notEmpty' ],
           STRING              : [ 'startsWith', 'notStartsWith', 'contains', 'notContains', 'endsWith', 'notEndsWith', 'equals', 'notEquals', 'empty', 'notEmpty' ],
           TEXT                : [ 'startsWith', 'notStartsWith', 'contains', 'notContains', 'endsWith', 'notEndsWith', 'equals', 'notEquals', 'empty', 'notEmpty' ],
           TIME                : [ 'equals', 'notEquals', 'lt', 'lte', 'gt', 'gte', 'empty', 'notEmpty' ],
           TIMERULE            : [ 'startsWith', 'notStartsWith', 'contains', 'notContains', 'endsWith', 'notEndsWith', 'equals', 'notEquals', 'empty', 'notEmpty' ],
           TIMESTAMP           : [ 'equals', 'notEquals', 'lt', 'lte', 'gt', 'gte', 'empty', 'notEmpty' ]
       },
             valueTypeToFilterMatchModeDropdownTypeMapping    = {
                 BOOLEAN             : 'none',
                 COLOR               : 'alphanumeric',
                 COORDINATE          : 'alphanumeric',
                 CURRENCY            : 'numeric',
                 DATE                : 'numeric',
                 DOUBLE              : 'numeric',
                 DURATION            : 'numeric',
                 EMAIL               : 'alphanumeric',
                 IBAN                : 'alphanumeric',
                 IMAGEDATA           : 'alphanumeric',
                 INTEGER             : 'numeric',
                 JSON                : 'alphanumeric',
                 LINK                : 'alphanumeric',
                 PHONE               : 'alphanumeric',
                 REFDIRECTION        : 'none',
                 REFTYPEWITHDIRECTION: 'none',
                 STATUS              : 'none',
                 STATUSID            : 'none',
                 STATUSNAME          : 'none',
                 STRING              : 'alphanumeric',
                 TEXT                : 'alphanumeric',
                 TIME                : 'numeric',
                 TIMERULE            : 'alphanumeric',
                 TIMESTAMP           : 'numeric'
             };

export const defaultProps = {
    disabled: false as boolean,

    /**
     * Should the field marked if dirty.
     */
    markDirty: true as boolean,

    markError: false as boolean,

    /**
     * Should a match mode selector shown at the filter field.
     */
    showMatchModeSelector: false as boolean,

    showMatchField: true as boolean,

    /**
     * Is the custom matchMode available.
     */
    hasCustomMatchMode: true as boolean,

    adjustWidth            : true as boolean,
    showOperatorForValueSet: false as boolean,
    matchModeLongNames     : false as boolean,

    /**
     * Any additionally needed params for getting the filter field.
     * Like schema, rawMode, showAdditionalRawNames, valueSetId, ...
     */
    params: {} as FilterConfigSet,

    classNames: [] as string[]
};

type DefaultProps = typeof defaultProps;

type Props = Partial<DefaultProps> & {

    fieldKey : string;

    /**
     * The data field the filter belongs to.
     */
    field : string;

    /**
     * The value for the filter field.
     */
    value? : any;

    /**
     * The type of the filter fields value.
     */
    valueType : FilterValueType;

    /**
     * The match mode for the filter field.
     */
    matchMode? : FilterMatchMode;

    matchFieldPlaceholder? : string;

    /**
     * Callback to invoke if filter fields value changes.
     *
     * @param {string} field                    The data field the filter field belongs to
     * @param {FilterMatchMode} matchMode       The filter fields match mode
     * @param {any} value                       The value of the filter field
     * @param {FilterValueType} FilterValueType The valueType of the filter field
     */
    onChange? : ( key : string, field : string, matchMode : FilterMatchMode, value : any, valueType : FilterValueType ) => void;

    /**
     * Callback to invoke if the enter key is pressed within the filter field.
     */
    onEnterKey? : () => void;

    /**
     * Debounce time in ms before {@link onChange} is invoked.
     *
     * The local state (input UI) is updated synchronously on every keystroke,
     * but the upward propagation via `onChange` is delayed until the user pauses.
     * Pending changes are flushed automatically on Enter and on unmount.
     *
     * Defaults to 0 (no debounce).
     */
    changeDebounceMs? : number;

    alternativeValueField? : AlternativeValueField;
}

type StateProps = {

    /**
     * The current user.
     *
     * Will be mapped from global state.
     */
    user? : User;
};

export type ComponentProps = Partial<DefaultProps> & Props;
type AllProps = DefaultProps & Props & StateProps;


type State = {
    domField : string;

    value : any;
    matchMode? : FilterMatchMode;

    lastValue : any;
    lastMatchMode? : FilterMatchMode;

    lastGivenValue : any;
    lastGivenMatchMode? : FilterMatchMode;

    isActive : boolean;

    dateTimeInputMode : 'relative' | 'absolute';
    relativeDateTimeEditValue? : string;

    dontUpdateCustomFilter : boolean;
    customFilterFocusToggle : boolean;
    customFilters : CustomFilter[];
    customFilterMode : CustomFilterMode;

    wrapperWidth? : string;
    wrapperCellStyle? : {
        overflow : string;
        position : string;
        zIndex : string;
    };

    alternativeValueFieldActive : boolean;
    lastAlternativeValueFieldActive? : boolean;
};

type FieldType = ( HTMLInputElement | Component ) & {
    hide? : () => void;
    getOverlay? : () => HTMLElement;
};


/**
 * filter field wrapper
 */
export class FilterField extends Component<AllProps, State> {

    static defaultProps = defaultProps;

    onDocumentKeyBound : boolean = false;
    onDocumentMouseBound : boolean = false;

    field : FieldType | null = null;
    matchModeDropdown : Dropdown | null = null;
    customFilterFields : ( FilterField | null )[] = [];
    overlayPanel : OverlayPanel | null = null;
    customFilterModeDropdown : Dropdown | null = null;

    private propOnChangeTimer? : ReturnType<typeof setTimeout>;
    private pendingPropOnChange? : () => void;

    /**
     * @param props
     */
    constructor( props : AllProps ) {
        super( props );

        this.state = {
            domField : props.field.replace( /(\.|:| |\(|\))/g, '_' ),
            value    : props.value,
            matchMode: props.matchMode,

            lastValue    : props.value,
            lastMatchMode: props.matchMode,

            lastGivenValue    : props.value,
            lastGivenMatchMode: props.matchMode,

            isActive         : false,
            dateTimeInputMode: 'absolute',

            dontUpdateCustomFilter : false,
            customFilterFocusToggle: false,
            customFilters          : props.matchMode === 'custom'
                ? getCustomFilters( props.value, props.valueType )
                : [],
            customFilterMode       : props.matchMode === 'custom'
                ? getCustomFilterMode( props.value )
                : 'AND',

            alternativeValueFieldActive: props.alternativeValueField?.active ?? false
        };

        // memoize some methods for performance
        this.getFilterField = memoize( this.getFilterField );
        this.getFilterValue = memoize( this.getFilterValue );
        this.getFilterMatchModeDropdown = memoize( this.getFilterMatchModeDropdown );

        // bind event handlers
        this.onDocumentKeyUp = this.onDocumentKeyUp.bind( this );
        this.onDocumentMouseClick = this.onDocumentMouseClick.bind( this );
        this.onDateTimeInputModeToggle = this.onDateTimeInputModeToggle.bind( this );
        this.onAlternativeValueFieldActiveToggle = this.onAlternativeValueFieldActiveToggle.bind( this );
        this.onFilterMatchModeChange = this.onFilterMatchModeChange.bind( this );
        this.onInputNumberFilterBlur = this.onInputNumberFilterBlur.bind( this );
        this.onFilterChange = this.onFilterChange.bind( this );
        this.onRelativeDateTimeFilterChange = this.onRelativeDateTimeFilterChange.bind( this );
        this.onCustomFilterModeChange = this.onCustomFilterModeChange.bind( this );
        this.onAddCustomFilter = this.onAddCustomFilter.bind( this );
        this.toggleCustomFilterFocus = this.toggleCustomFilterFocus.bind( this );
        this.onMouseEnter = this.onMouseEnter.bind( this );
        this.onMouseLeave = this.onMouseLeave.bind( this );
    }

    /**
     * REACT lifecycle method
     */
    static getDerivedStateFromProps( props : Readonly<AllProps>, state : Readonly<State> ) {
        const retVal : Partial<State> = {
            value            : state.value,
            matchMode        : state.matchMode,
            dateTimeInputMode: state.dateTimeInputMode
        };

        // update state
        if ( props.value !== state.lastGivenValue ) {
            retVal.lastGivenValue = props.value;
            retVal.lastValue = props.value;
            retVal.value = props.value;
            retVal.dontUpdateCustomFilter = false;
        }
        else if ( state.value !== state.lastValue ) {
            retVal.lastValue = state.value;
        }

        if ( props.matchMode !== state.lastGivenMatchMode ) {
            retVal.lastGivenMatchMode = props.matchMode;
            retVal.lastMatchMode = props.matchMode;
            retVal.matchMode = props.matchMode;
            retVal.dontUpdateCustomFilter = false;
        }
        else if ( state.matchMode !== state.lastMatchMode ) {
            retVal.lastMatchMode = state.matchMode;
        }

        if ( props.alternativeValueField?.active ?? false !== state.lastAlternativeValueFieldActive ) {
            retVal.alternativeValueFieldActive = props.alternativeValueField?.active ?? false;
            retVal.lastAlternativeValueFieldActive = retVal.alternativeValueFieldActive;
        }

        // compute custom filter values?
        if (
            !state.dontUpdateCustomFilter
            && ( retVal.matchMode === 'custom' || state.lastMatchMode === 'custom' )
            && ( retVal.value !== state.lastValue || retVal.matchMode !== state.lastMatchMode )
        ) {
            if ( retVal.matchMode === 'custom' ) {
                if ( state.lastMatchMode && state.lastMatchMode !== 'custom' ) {
                    retVal.value = getValueStringFromMatchModeAndValue( state.lastMatchMode, retVal.value );
                }

                retVal.customFilters = getCustomFilters( retVal.value, props.valueType );
                retVal.customFilterMode = getCustomFilterMode( retVal.value );
            }
            else {
                retVal.customFilters = [];
                retVal.customFilterMode = 'AND';
            }
        }

        // compute dateTimeInputMode
        if ( retVal.matchMode !== 'custom' && ( props.valueType === 'DATE' || props.valueType === 'TIME' || props.valueType === 'TIMESTAMP' ) && ( retVal.value ?? '' ).indexOf( 'now' ) !== -1 ) {
            retVal.dateTimeInputMode = 'relative';
        }

        return retVal;
    }

    /**
     * REACT lifecycle method
     */
    componentDidMount() {
        this.toggleDocumentKey();
        this.toggleDocumentMouse();
    }

    /**
     * REACT lifecycle method
     */
    componentWillUnmount() {
        this.toggleDocumentKey( true );
        this.toggleDocumentMouse( true );
        this.flushPendingPropOnChange();
    }

    /**
     * REACT lifecycle method
     */
    componentDidUpdate( prevProps : Props, prevState : Object ) {
        const {
                  disabled
              } = this.props, {
                  wrapperWidth,
                  wrapperCellStyle,
                  matchMode,
                  isActive,
                  domField
              } = this.state;

        this.toggleDocumentKey();
        this.toggleDocumentMouse();

        if ( wrapperWidth ?? wrapperCellStyle ) {
            const wrapperElement : HTMLElement | null = document.getElementById( `filterField_wrapper_${this.state.domField}` ) as HTMLElement,
                  wrapperCellElement                  = wrapperElement?.closest( 'th' ) ?? wrapperElement.closest( 'td' );

            if ( wrapperCellElement ) {
                const allFilterElements = wrapperElement?.closest( 'tr' )?.children;

                let wrapperCellElementIndex = 0;

                if ( allFilterElements ) {
                    for ( let i = 0; i < allFilterElements.length; i++ ) {
                        if ( allFilterElements.item( i ) === wrapperCellElement ) {
                            wrapperCellElementIndex = i;
                            break;
                        }
                    }
                }

                if ( wrapperWidth && !wrapperCellStyle ) {
                    this.setState( {
                        wrapperCellStyle: {
                            overflow: wrapperCellElement.style.overflow,
                            position: wrapperCellElement.style.position,
                            zIndex  : wrapperCellElement.style.zIndex
                        }
                    } );

                    wrapperCellElement.style.overflow = 'visible';
                    wrapperCellElement.style.position = 'relative';
                    wrapperCellElement.style.zIndex = `${10000 + ( allFilterElements?.length ?? 0 ) - wrapperCellElementIndex}`;
                }
                else if ( !wrapperWidth && wrapperCellStyle ) {
                    const cellEl = wrapperCellElement;

                    if ( this.matchModeDropdown ) {
                        this.matchModeDropdown.hide();
                    }

                    if ( this.overlayPanel ) {
                        this.overlayPanel.hide();
                    }

                    if ( this.field?.hide ) {
                        this.field.hide();
                    }

                    setTimeout(
                        () => {
                            cellEl.style.overflow = wrapperCellStyle.overflow;
                            cellEl.style.position = wrapperCellStyle.position;
                            cellEl.style.zIndex = wrapperCellStyle.zIndex;

                            this.setState( {
                                wrapperCellStyle: undefined
                            } );
                        },
                        500
                    )
                }
            }
        }

        setTimeout( () => {
            if ( this.overlayPanel ) {
                if ( !disabled && matchMode === 'custom' && isActive && !document.getElementById( `${domField}_matchModeDropdown` )?.classList.contains( 'p-focus' ) ) {
                    this.overlayPanel.show( null, document.getElementById( domField ) );
                }
                else {
                    this.overlayPanel.hide();
                }
            }
        }, 10 );
    }

    /**
     * Adds or removes an inside keyUp handler.
     *
     * @private
     */
    toggleDocumentKey( unmount : boolean = false ) {
        if ( this.props.onEnterKey ) {
            if ( !this.onDocumentKeyBound ) {
                this.onDocumentKeyBound = true;
                document.addEventListener( 'keyup', this.onDocumentKeyUp );
            }
            else if ( unmount && this.onDocumentKeyBound ) {
                this.onDocumentKeyBound = false;
                document.removeEventListener( 'keyup', this.onDocumentKeyUp );
            }
        }
    }

    /**
     * Adds or removes a click handler.
     *
     * @private
     */
    toggleDocumentMouse( unmount : boolean = false ) {
        if ( !this.onDocumentMouseBound ) {
            this.onDocumentMouseBound = true;
            document.addEventListener( 'click', this.onDocumentMouseClick, true );
        }
        else if ( unmount && this.onDocumentMouseBound ) {
            this.onDocumentMouseBound = false;
            document.removeEventListener( 'click', this.onDocumentMouseClick, true );
        }
    }

    /**
     * Gets the filter field.
     *
     * @param {any} value
     * @param {FilterMatchMode} matchMode
     * @param {boolean} disabled
     * @param {String} language
     * @param {FilterConfigSet} [params={}]
     *
     * @return {JSX.Element}
     *
     * @private
     */
    getFilterField( value : any, matchMode : FilterMatchMode, disabled : boolean, language : string, params : FilterConfigSet = {}, dateTimeInputMode : State[ 'dateTimeInputMode' ] ) {
        const {
                  matchFieldPlaceholder,
                  valueType
              }              = this.props,
              {
                  domField,
                  relativeDateTimeEditValue
              }              = this.state,
              filterValue    = this.getFilterValue( value, matchMode ),
              filterDisabled = disabled || matchMode === 'empty' || matchMode === 'notEmpty',
              placeholder    = matchMode === 'empty' || matchMode === 'notEmpty'
                  ? `[ ${__( `YN.base.operator.${matchMode}` )} ]`
                  : matchFieldPlaceholder ?? '';

        let retVal : JSX.Element = <></>,
            mode,
            currency,
            suffix,
            data : any[] | undefined,
            dataTemplate,
            selectedDataTemplate;

        params.schema = params.schema ?? 'DATA';

        if ( matchMode === 'custom' ) {
            retVal = <InputText
                ref={el => this.field = el}

                id={domField}

                value={filterValue}
                pt={{
                    root: {
                        onChange: this.onFilterChange,
                        onFocus : this.toggleCustomFilterFocus
                    }
                }}

                autoComplete="off"
                disabled={filterDisabled}
                placeholder={placeholder}
            />;
        }
        else if ( params.valueSetId ) {
            data = metaModel[ params.schema as Schema ].getValueSetValues( params.valueSetId ).map( ( valueObj : ValueObject ) => {
                const label = getValueDisplayName( params.valueSetId!, valueObj.id!, params.schema, params.rawMode, params.showAdditionalRawNames );
                return {
                    id         : valueObj.id,
                    value      : valueObj.value,
                    label,
                    parsedLabel: parse( label ),
                    sortOrder  : valueObj.sortOrder
                } as ValueObject & {
                    parsedLabel? : React.ReactNode
                };
            } );

            const selectAll = filterValue.length === data.length;

            // sort
            data.sort( ( val1 : ValueObject, val2 : ValueObject ) => {
                const listingOrder = params.listingOrder ?? 'order';

                if ( listingOrder === 'order' ) {
                    if ( ( val1.sortOrder ?? 0 ) > ( val2.sortOrder ?? 0 ) ) {
                        return 1;
                    }
                    else if ( ( val1.sortOrder ?? 0 ) < ( val2.sortOrder ?? 0 ) ) {
                        return -1;
                    }
                    else {
                        return 0;
                    }
                }
                else {
                    if ( ( val1.label ) > ( val2.label ) ) {
                        return listingOrder === 'asc' ? 1 : -1;
                    }
                    else if ( ( val1.label ) < ( val2.label ) ) {
                        return listingOrder === 'asc' ? -1 : 1;
                    }
                    else {
                        return 0;
                    }
                }
            } );

            data.push( {
                value    : '::empty::',
                label    : `[ ${__( `YN.base.operator.empty` )} ]`,
                matchMode: 'empty'
            } );
            data.push( {
                value    : '::notEmpty::',
                label    : `[ ${__( `YN.base.operator.notEmpty` )} ]`,
                matchMode: 'notEmpty'
            } );

            dataTemplate = ( option : any ) => {
                return option.parsedLabel ?? option.label;
            };

            selectedDataTemplate = ( option : any ) => {
                let innerRetVal = <>&nbsp;</>;

                if ( option ) {
                    const optionData = ( data as ( ValueObject & {
                        parsedLabel? : React.ReactNode
                    } )[] ).find( dataObj => dataObj.value === option );

                    innerRetVal = <div
                        className={`p-multiselect-token ${optionData?.value === '::empty::' || optionData?.value === '::notEmpty::' ? 'yn-chip-empty' : ''}`}>{optionData?.parsedLabel ?? optionData?.label ?? option}</div>;
                }

                return innerRetVal;
            };

            retVal = <MultiSelect
                ref={el => this.field = el}

                value={filterValue}
                onChange={this.onFilterChange}

                options={data}
                optionValue="value"
                optionLabel="label"

                display="chip"

                appendTo={document.body}
                showClear={false}
                selectAll={selectAll}
                onSelectAll={( event ) => {
                    if ( event.checked ) {
                        this.onFilterChange( { value: [] } );
                    }
                    else {
                        const newValue = data!.reduce(
                            ( innerRetVal, d ) => {
                                if ( d.id !== undefined ) {
                                    innerRetVal.push( d.value );
                                }

                                return innerRetVal;
                            },
                            [] as any[]
                        );

                        this.onFilterChange( { value: newValue } );
                    }
                }}
                maxSelectedLabels={3}
                itemTemplate={dataTemplate}
                selectedItemTemplate={selectedDataTemplate}
                disabled={disabled}
                style={{ width: '100%' }}
            />;
        }
        else {
            switch ( valueType ) {
                case 'BOOLEAN':
                    data = [ '', '0', '1', '::empty::', '::notEmpty::' ];
                    dataTemplate = ( option : string ) => {
                        let innerRetVal;

                        if ( option === '' ) {
                            innerRetVal = <div className="yn-option-placeholder"><span>[ any ]</span></div>;
                        }
                        else {
                            switch ( option ) {
                                case '0':
                                    if ( !params.rawMode ) {
                                        innerRetVal = <div className="yn-option">{boolRenderer( option )} <span>false</span></div>;
                                    }
                                    else {
                                        innerRetVal = <div className="yn-option"><span>{option}</span></div>;
                                    }
                                    break;

                                case '1':
                                    if ( !params.rawMode ) {
                                        innerRetVal = <div className="yn-option">{boolRenderer( option )} <span>true</span></div>;
                                    }
                                    else {
                                        innerRetVal = <div className="yn-option"><span>{option}</span></div>;
                                    }
                                    break;

                                case '::empty::':
                                    innerRetVal = <div className="yn-option">
                                        <span style={{
                                            paddingRight : '0.7rem',
                                            fontSize     : '1.4rem',
                                            lineHeight   : '1.2rem',
                                            verticalAlign: 'text-bottom'
                                        }}>∅</span>
                                        <span style={{ verticalAlign: 'middle' }}>[ {__( `YN.base.operator.empty` )} ]</span>
                                    </div>;
                                    break;

                                case '::notEmpty::':
                                    innerRetVal = <div className="yn-option">
                                        <span style={{
                                            textDecoration: 'line-through',
                                            paddingRight  : '0.7rem',
                                            fontSize      : '1.4rem',
                                            lineHeight    : '1.2rem',
                                            verticalAlign : 'text-bottom'
                                        }}>∅</span>
                                        <span style={{ verticalAlign: 'middle' }}>[ {__( `YN.base.operator.notEmpty` )} ]</span>
                                    </div>;
                                    break;
                            }
                        }

                        return innerRetVal;
                    };
                    selectedDataTemplate = ( option : string ) => {
                        let innerRetVal = <>&nbsp;</>;

                        if ( option ) {
                            switch ( option ) {
                                case '0':
                                    if ( !params.rawMode ) {
                                        innerRetVal = <div className="yn-option">{boolRenderer( option )} <span>false</span></div>;
                                    }
                                    else {
                                        innerRetVal = <div className="yn-option"><span>{option}</span></div>;
                                    }
                                    break;

                                case '1':
                                    if ( !params.rawMode ) {
                                        innerRetVal = <div className="yn-option">{boolRenderer( option )} <span>true</span></div>;
                                    }
                                    else {
                                        innerRetVal = <div className="yn-option"><span>{option}</span></div>;
                                    }
                                    break;

                                case '::empty::':
                                    innerRetVal = <div className="yn-option">
                                        <span style={{
                                            display      : 'inline-block',
                                            paddingRight : '0.7rem',
                                            fontSize     : '1.4rem',
                                            lineHeight   : '0.8rem',
                                            height       : '1rem',
                                            verticalAlign: 'text-bottom'
                                        }}>∅</span>
                                        [ {__( `YN.base.operator.empty` )} ]
                                    </div>;
                                    break;

                                case '::notEmpty::':
                                    innerRetVal = <div className="yn-option">
                                        <span style={{
                                            textDecoration: 'line-through',
                                            display       : 'inline-block',
                                            paddingRight  : '0.7rem',
                                            fontSize      : '1.4rem',
                                            lineHeight    : '0.8rem',
                                            height        : '1rem',
                                            verticalAlign : 'text-bottom'
                                        }}>∅</span>
                                        [ {__( `YN.base.operator.notEmpty` )} ]
                                    </div>;
                                    break;
                            }
                        }

                        return innerRetVal;
                    };

                    retVal = <Dropdown
                        ref={el => this.field = el}

                        id={domField}

                        value={filterValue}
                        onChange={this.onFilterChange}

                        options={data}

                        appendTo={document.body}
                        itemTemplate={dataTemplate}
                        valueTemplate={selectedDataTemplate}
                        disabled={disabled}
                        className="yn-inputgroup"
                        style={{ width: '100%' }}
                    />;
                    break;

                case 'DATE':
                    retVal = <div
                        className="p-inputgroup yn-inputgroup"
                        style={{ width: '100%' }}
                    >
                        {dateTimeInputMode === 'absolute' &&
                            <DateField
                                ref={el => this.field = el}

                                uid={domField}

                                value={filterValue !== '' ? filterValue : undefined}
                                onChange={this.onFilterChange}
                                appendToElementId="document"
                                labelVisibility="hidden"
                                infoTextLayout="hidden"
                                style={{ width: '100%', margin: 0 }}
                                pt={{
                                    input         : {
                                        root: { padding: '.5rem 0 .5rem .5rem' }
                                    },
                                    dropdownButton: {
                                        root: { marginRight: '0' }
                                    }
                                }}

                                showButtonBar={true}

                                disabled={filterDisabled}
                                placeholder={placeholder}
                            />
                        }
                        {dateTimeInputMode !== 'absolute' &&
                            <InputText
                                ref={el => this.field = el}

                                id={domField}

                                value={relativeDateTimeEditValue ?? filterValue}
                                pt={{
                                    root: {
                                        onChange: this.onRelativeDateTimeFilterChange
                                    }
                                }}

                                disabled={filterDisabled}
                                placeholder={placeholder}
                            />
                        }
                        <Button
                            icon={`pi ${dateTimeInputMode === 'absolute' ? 'pi-clock' : 'pi-history'}`}
                            style={{ alignSelf: 'center' }}
                            tooltip={__( `YN.base.dateTime.inputMode.switchTo${dateTimeInputMode === 'absolute' ? 'Relative' : 'Absolute'}` )}
                            tooltipOptions={{ showDelay: 1500 }}
                            onClick={this.onDateTimeInputModeToggle}
                        />
                    </div>;
                    break;

                case 'CURRENCY':
                case 'DOUBLE':
                case 'DURATION':
                    if ( params.valueUnit && isIntlNumberFormatCurrency( params.valueUnit ) ) {
                        mode = 'currency';
                        currency = params.valueUnit;
                    }
                    else {
                        mode = 'decimal';
                        if ( params.valueUnit ) {
                            if ( isIntlNumberFormatUnit( params.valueUnit ) ) {
                                suffix = ` ${Intl.NumberFormat( undefined, { style: 'unit', unit: params.valueUnit } ).format( 999 ).replace( /^999 /, '' )}`;
                            }
                            else {
                                suffix = ` ${params.valueUnit}`;
                            }
                        }
                    }

                    retVal = <InputNumber
                        ref={el => this.field = el}

                        id={domField}

                        value={filterValue}
                        onChange={this.onFilterChange}
                        onBlur={this.onInputNumberFilterBlur}

                        mode={mode as InputNumberProps['mode']}
                        currency={currency}
                        suffix={suffix}
                        minFractionDigits={currency
                            ? 2
                            : 1}
                        maxFractionDigits={currency
                            ? 2
                            : 6}
                        inputStyle={{ textAlign: 'right' }}
                        style={{ width: '100%' }}

                        disabled={filterDisabled}
                        placeholder={placeholder}
                    />;
                    break;

                case 'INTEGER':
                    if ( params.valueUnit && isIntlNumberFormatCurrency( params.valueUnit ) ) {
                        mode = 'currency';
                        currency = params.valueUnit;
                    }
                    else {
                        mode = 'decimal';
                        if ( params.valueUnit ) {
                            suffix = params.valueUnit;
                        }
                    }

                    retVal = <InputNumber
                        ref={el => this.field = el}

                        id={domField}

                        value={filterValue}
                        onChange={this.onFilterChange}
                        onBlur={this.onInputNumberFilterBlur}

                        mode={mode as InputNumberProps['mode']}
                        currency={currency}
                        suffix={suffix}
                        minFractionDigits={currency
                            ? 2
                            : 0}
                        maxFractionDigits={currency
                            ? 2
                            : 0}
                        inputStyle={{ textAlign: 'right' }}
                        style={{ width: '100%' }}

                        disabled={filterDisabled}
                        placeholder={placeholder}
                    />;
                    break;

                case 'REFDIRECTION':
                    data = [ '', 'src', 'dest' ];
                    dataTemplate = ( option : '' | 'asc' | 'desc' | 'src' | 'dest' ) => {
                        let innerRetVal;

                        if ( option === '' ) {
                            innerRetVal = <div className="yn-option-placeholder"><span>[ any ]</span></div>;
                        }
                        else {
                            innerRetVal = <div
                                className="yn-option">{directionRenderer( option, params.rawMode, params.showAdditionalRawNames )}</div>;
                        }

                        return innerRetVal;
                    };
                    selectedDataTemplate = ( option : 'asc' | 'desc' | 'src' | 'dest' ) => {
                        return <div
                            className="yn-option">{directionRenderer( option, params.rawMode, params.showAdditionalRawNames )}</div>;
                    };

                    retVal = <Dropdown
                        ref={el => this.field = el}

                        id={domField}

                        value={filterValue}
                        onChange={this.onFilterChange}

                        options={data}
                        appendTo={document.body}
                        itemTemplate={dataTemplate}
                        valueTemplate={selectedDataTemplate}
                        className="yn-inputgroup"
                        style={{ width: '100%' }}

                        disabled={filterDisabled}
                    />;
                    break;

                case 'STATUS':
                case 'STATUSID':
                case 'STATUSNAME':
                    data = metaModel[ params.schema as Schema ].getStatuses();

                    // sort
                    data.sort( ( val1, val2 ) => {
                        if ( val1.description > val2.description ) {
                            return 1;
                        }
                        else if ( val1.description < val2.description ) {
                            return -1;
                        }
                        else {
                            return 0;
                        }
                    } );

                    // template
                    dataTemplate = ( option : any ) => {
                        let innerRetVal : string | JSX.Element = statusRenderer( option[ valueType === 'STATUSNAME' ? 'name' : 'id' ], params.schema!, params.rawMode, params.showAdditionalRawNames );

                        innerRetVal = statusColoringRenderer( innerRetVal, option.id, params.schema );

                        return innerRetVal;
                    };
                    selectedDataTemplate = ( option : any ) => {
                        let innerRetVal : string | JSX.Element = <>&nbsp;</>;

                        if ( option ) {
                            innerRetVal = statusRenderer( option, params.schema!, params.rawMode, params.showAdditionalRawNames );
                            innerRetVal = statusColoringRenderer( innerRetVal, option, params.schema );
                            innerRetVal = <div className="p-multiselect-token">{innerRetVal}</div>;
                        }

                        return innerRetVal;
                    };

                    retVal = <MultiSelect
                        ref={el => this.field = el}

                        id={domField}

                        value={filterValue}
                        onChange={this.onFilterChange}

                        options={data}
                        optionValue={valueType === 'STATUSNAME'
                            ? 'name'
                            : 'id'}
                        optionLabel="name"

                        display="chip"

                        appendTo={document.body}
                        showClear={false}
                        maxSelectedLabels={3}
                        itemTemplate={dataTemplate}
                        selectedItemTemplate={selectedDataTemplate}
                        className="yn-inputgroup"
                        style={{ width: '100%' }}

                        disabled={disabled}
                    />;
                    break;

                case 'TIME':
                    retVal = <div
                        className="p-inputgroup yn-inputgroup"
                        style={{ width: '100%' }}
                    >
                        {dateTimeInputMode === 'absolute' &&
                            <TimeField
                                ref={el => this.field = el}

                                uid={domField}

                                value={filterValue !== '' ? filterValue : undefined}
                                onChange={this.onFilterChange}
                                appendToElementId="document"
                                labelVisibility="hidden"
                                infoTextLayout="hidden"
                                style={{ width: '100%', margin: 0 }}
                                pt={{
                                    input: {
                                        root: { padding: '.5rem 0 .5rem .5rem' }
                                    }
                                }}

                                showButtonBar={true}

                                disabled={filterDisabled}
                                placeholder={placeholder}
                            />
                        }
                        {dateTimeInputMode !== 'absolute' &&
                            <InputText
                                ref={el => this.field = el}

                                id={domField}

                                value={relativeDateTimeEditValue ?? filterValue}
                                pt={{
                                    root: {
                                        onChange: this.onFilterChange
                                    }
                                }}

                                disabled={filterDisabled}
                                placeholder={placeholder}
                            />
                        }
                        <Button
                            icon={`pi ${dateTimeInputMode === 'absolute' ? 'pi-clock' : 'pi-history'}`}
                            style={{ alignSelf: 'center' }}
                            tooltip={__( `YN.base.dateTime.inputMode.switchTo${dateTimeInputMode === 'absolute' ? 'Relative' : 'Absolute'}` )}
                            tooltipOptions={{ showDelay: 1500 }}
                            onClick={this.onDateTimeInputModeToggle}
                        />
                    </div>;
                    break;

                case 'TIMESTAMP':
                    retVal = <div
                        className="p-inputgroup yn-inputgroup"
                        style={{ width: '100%' }}
                    >
                        {dateTimeInputMode === 'absolute' &&
                            <TimestampField
                                ref={el => this.field = el}

                                uid={domField}

                                timezone={params.timezone ?? 'Europe/Berlin'}

                                value={filterValue !== '' ? filterValue : undefined}
                                onChange={this.onFilterChange}
                                appendToElementId="document"
                                labelVisibility="hidden"
                                infoTextLayout="hidden"
                                style={{ width: '100%', margin: 0 }}
                                pt={{
                                    input: {
                                        root: { padding: '.5rem 0 .5rem .5rem' }
                                    }
                                }}

                                showButtonBar={true}

                                disabled={filterDisabled}
                                placeholder={placeholder}
                            />
                        }
                        {dateTimeInputMode !== 'absolute' &&
                            <InputText
                                ref={el => this.field = el}

                                id={domField}

                                value={relativeDateTimeEditValue ?? filterValue}
                                pt={{
                                    root: {
                                        onChange: this.onFilterChange
                                    }
                                }}

                                disabled={filterDisabled}
                                placeholder={placeholder}
                            />
                        }
                        <Button
                            icon={`pi ${dateTimeInputMode === 'absolute' ? 'pi-clock' : 'pi-history'}`}
                            style={{ alignSelf: 'center' }}
                            tooltip={__( `YN.base.dateTime.inputMode.switchTo${dateTimeInputMode === 'absolute' ? 'Relative' : 'Absolute'}` )}
                            tooltipOptions={{ showDelay: 1500 }}
                            onClick={this.onDateTimeInputModeToggle}
                        />
                    </div>;
                    break;

                case 'COLOR':
                case 'COORDINATE':
                case 'EMAIL':
                case 'IBAN':
                case 'IMAGEDATA':
                case 'JSON':
                case 'LINK':
                case 'PHONE':
                case 'TIMERULE':
                case 'STRING':
                case 'TEXT':
                    retVal = <InputText
                        ref={el => this.field = el}

                        id={domField}

                        value={filterValue}
                        pt={{
                            root: {
                                onChange: this.onFilterChange
                            }
                        }}

                        disabled={filterDisabled}
                        placeholder={placeholder}
                    />;
                    break;
            }
        }

        return retVal;
    }

    /**
     * Gets the matchMode dropdown.
     *
     * @param {FilterMatchMode} matchMode
     * @param {boolean} disabled
     * @param {String} language
     *
     * @return {JSX.Element}
     *
     * @private
     */
    getFilterMatchModeDropdown( matchMode : FilterMatchMode, disabled : boolean, language : string, params : FilterConfigSet = {} ) : JSX.Element | null {
        const {
                  hasCustomMatchMode,
                  matchModeLongNames,
                  showOperatorForValueSet,
                  valueType
              }                    = this.props,
              {
                  domField
              }                    = this.state,
              operatorDropdownType = valueTypeToFilterMatchModeDropdownTypeMapping[ valueType ];

        let retVal : JSX.Element | null = null,
            data                        = params.valueSetId
                ? [ 'equals', 'notEquals', 'empty', 'notEmpty' ]
                : valueTypeToFilterMatchModeDropdownOptionsMapping[ valueType ];

        if ( hasCustomMatchMode ) {
            data = ( [] as string[] ).concat( data );
            data.push( 'custom' );
        }

        if ( data.length && operatorDropdownType !== 'none' && ( !params?.valueSetId || showOperatorForValueSet ) ) {
            const dataTemplate         = ( option : any ) => {
                      if ( matchModeLongNames ) {
                          return __( `YN.base.operator.${option}` );
                      }
                      else {
                          return <>
                          <span className="yn-operator-icon">
                              {parse( __( `YN.base.operator.${option}.symbol` ) )}
                          </span>
                              {__( `YN.base.operator.${option}` )}
                          </>;
                      }
                  },
                  selectedDataTemplate = ( option : any ) => {
                      let innerRetVal : string | React.ReactElement = <>&nbsp;</>;

                      if ( option ) {
                          if ( matchModeLongNames ) {
                              innerRetVal = <div className="yn-operator-dropdown-label">
                                  {__( `YN.base.operator.${option}` )}
                              </div>;
                          }
                          else {
                              innerRetVal = <div className="yn-operator-dropdown-label">
                                  {parse( __( `YN.base.operator.${option}.symbol` ) )}
                              </div>;
                          }
                      }

                      return innerRetVal;
                  };

            retVal = <Dropdown
                ref={el => this.matchModeDropdown = el}

                id={`${domField}_matchModeDropdown`}

                value={matchMode}
                options={data}
                appendTo={document.body}
                disabled={disabled}
                itemTemplate={dataTemplate}
                valueTemplate={selectedDataTemplate}
                className={`yn-inputgroup yn-operator-dropdown yn-operator-dropdown-${operatorDropdownType}`}
                onChange={this.onFilterMatchModeChange}
                showOnFocus={true}
                onFocus={this.toggleCustomFilterFocus}
            />;
        }

        return retVal;
    }

    /**
     * Gets the filter field.
     *
     * @param {any} value
     * @param {FilterMatchMode} matchMode
     * @param {boolean} disabled
     * @param {String} language
     * @param {FilterConfigSet} [params={}]
     *
     * @return {JSX.Element}
     *
     * @private
     */
    getFilterCustomOverlay( value : any, matchMode : FilterMatchMode, disabled : boolean, language : string, params : FilterConfigSet = {} ) {
        const {
                  fieldKey
              }                       = this.props,
              {
                  customFilterMode,
                  customFilters,
                  domField,
                  isActive
              }                       = this.state,
              fields : JSX.Element[ ] = [];

        if ( matchMode === 'custom' && isActive ) {
            for ( let customFilterIndex = 0; customFilterIndex < customFilters.length; customFilterIndex++ ) {
                const customFilter = customFilters[ customFilterIndex ];

                fields.push( <div
                    key={`${domField}_customFilter_${customFilterIndex}`}
                    id={`${domField}_customFilter_${customFilterIndex}`}
                    className="flex align-items-center mb-2 yn-sorter"
                >
                    <Button
                        onClick={this.onRemoveCustomFilter.bind( this, customFilterIndex )}
                        icon="pi pi-times"
                        className="p-button-rounded p-button-danger p-button-text mr-2"
                    />
                    <FilterField
                        {...this.props}

                        ref={el => {
                            this.customFilterFields[ customFilterIndex ] = el;
                        }}

                        fieldKey={`${fieldKey}_${customFilterIndex}`}
                        field={`${domField}_customFilter_${customFilterIndex}_field`}

                        value={customFilter.value}
                        matchMode={customFilter.matchMode}

                        disabled={disabled}
                        markDirty={false}
                        showMatchModeSelector={true}
                        showMatchField={true}
                        hasCustomMatchMode={false}

                        onChange={this.onCustomFilterChange.bind( this, customFilterIndex )}
                    />
                </div> );
            }
        }

        return <OverlayPanel
            ref={el => this.overlayPanel = el}

            appendTo={document.body}
            dismissable={false}

            className="yn-customfilterselector"
            style={{ width: '20rem' }}
        >
            <label
                key={`${domField}_header`}

                className="yn-menu-title-label"
            >
                {__( 'YN.base.filter.custom.title' )}
            </label>
            <Dropdown
                ref={el => this.customFilterModeDropdown = el}

                value={customFilterMode}

                options={[
                    {
                        value: 'AND',
                        label: __( 'YN.base.filter.custom.matchAll' )
                    },
                    {
                        value: 'OR',
                        label: __( 'YN.base.filter.custom.matchAny' )
                    }
                ]}
                optionValue="value"
                optionLabel="label"

                onChange={this.onCustomFilterModeChange}
                style={{ width: '100%' }}
            />
            <hr className="mt-3 mb-3 yn-ruler"/>

            {fields}
            <hr className="mt-3 mb-3 yn-ruler"/>

            <Button
                label={__( 'YN.base.filter.custom.add' )}
                onClick={this.onAddCustomFilter}
                className="p-button-outlined"
                style={{ width: '100%' }}
            />
        </OverlayPanel>;
    }

    updateFilterValue( value : any, matchMode : FilterMatchMode ) {
        this.setState( { value, matchMode } );
    }

    /**
     * Converts value to the appropriate type for the filter field
     *
     * @param {any} value
     * @param {FilterMatchMode} matchMode
     *
     * @return {*}
     *
     * @private
     */
    getFilterValue( value : any, matchMode : FilterMatchMode ) {
        const {
                  valueType,
                  params
              } = this.props;

        let retVal = value;

        if ( matchMode === 'custom' ) {
            if ( retVal === undefined || retVal === null || retVal === '' ) {
                retVal = '';
            }
            else {
                retVal = String( retVal );
            }
        }
        else if ( params.valueSetId ) {
            // translate matchMode 'empty' to value '::empty::'
            if ( matchMode === 'empty' ) {
                retVal = [ '::empty::' ];
            }
            // translate matchMode 'notEmpty' to value '::notEmpty::'
            else if ( matchMode === 'notEmpty' ) {
                retVal = [ '::notEmpty::' ];
            }
            // convert string to array
            else if ( retVal !== undefined && retVal !== null && retVal !== '' ) {
                retVal = retVal.split( '||' );
            }
            // default
            else {
                retVal = [];
            }
        }
        else {
            switch ( valueType ) {
                case 'BOOLEAN':
                    // translate matchMode 'empty' to value '::empty::'
                    if ( matchMode === 'empty' ) {
                        retVal = '::empty::';
                    }
                    // translate matchMode 'notEmpty' to value '::notEmpty::'
                    else if ( matchMode === 'notEmpty' ) {
                        retVal = '::notEmpty::';
                    }
                    break;

                case 'DATE':
                    if ( retVal === undefined || retVal === null || retVal === '' ) {
                        retVal = '';
                    }
                    else if ( !value.match( /^now(( (\+|\-) ([0-9]+) (seconds?|minutes?|hours?|days?|months?|years?))*)$/i ) ) {
                        retVal = moment( retVal, 'YYYY-MM-DD' ).toDate();
                    }
                    break;

                case 'CURRENCY':
                case 'DOUBLE':
                case 'DURATION':
                    if ( retVal === undefined || retVal === null || retVal === '' ) {
                        retVal = undefined;
                    }
                    else if ( retVal !== '-' ) {
                        retVal = Number( retVal );

                        if ( isNaN( retVal ) ) {
                            retVal = undefined;
                        }
                    }
                    break;

                case 'INTEGER':
                    if ( retVal === undefined || retVal === null || retVal === '' ) {
                        retVal = undefined;
                    }
                    else if ( retVal !== '-' ) {
                        retVal = parseInt( retVal );

                        if ( isNaN( retVal ) ) {
                            retVal = undefined;
                        }
                    }
                    break;

                case 'STATUS':
                case 'STATUSID':
                case 'STATUSNAME':
                    if ( retVal === undefined || retVal === null || retVal === '' ) {
                        retVal = [];
                    }
                    else {
                        retVal = retVal.split( '||' );
                        if ( valueType !== 'STATUSNAME' ) {
                            retVal = retVal.map( ( filterValue : string ) => parseInt( filterValue ) );
                        }
                    }
                    break;

                case 'TIME':
                    if ( retVal === undefined || retVal === null || retVal === '' ) {
                        retVal = '';
                    }
                    else if ( !value.match( /^now(( (\+|\-) ([0-9]+) (seconds?|minutes?|hours?|days?|months?|years?))*)$/i ) ) {
                        retVal = moment( retVal, 'HH:mm:ss' ).toDate();
                    }
                    break;

                case 'TIMESTAMP':
                    if ( retVal === undefined || retVal === null || retVal === '' ) {
                        retVal = '';
                    }
                    break;

            }
        }

        return retVal;
    }

    /**
     * Gets the default matchMode for FilterFields valueType
     *
     * @return {FilterMatchMode}
     */
    getDefaultMatchMode() : FilterMatchMode {
        let retVal : FilterMatchMode;

        // ValueSet DropDown
        if ( this.props.params?.valueSetId ) {
            retVal = 'equals';
        }
        else {
            retVal = getDefaultFilterMatchMode( this.props.valueType );
        }

        return retVal;
    }

    toggleCustomFilterFocus() {
        this.setState( { customFilterFocusToggle: !this.state.customFilterFocusToggle } );
    }

    /**
     * Handler for document key up event
     *
     * @private
     */
    onDocumentKeyUp( event : KeyboardEvent ) {
        if ( event.key === 'Enter' && this.props.onEnterKey ) {
            const filterFieldWrapper = document.getElementById( `filterField_wrapper_${this.state.domField}` ),
                  isInside           = filterFieldWrapper && event.target
                      ? filterFieldWrapper.isSameNode( event.target as Node ) || filterFieldWrapper.contains( event.target as Node )
                      : false;

            if ( isInside ) {
                if (
                    (
                        this.props.valueType === 'CURRENCY'
                        || this.props.valueType === 'DURATION'
                        || this.props.valueType === 'DOUBLE'
                        || this.props.valueType === 'INTEGER'
                    )
                    && this.props.value === '-'
                ) {
                    this.onFilterChange( { value: '' } );
                }

                this.flushPendingPropOnChange();
                this.props.onEnterKey();
            }
        }

        this.checkForDocumentActiveElement();
    }

    /**
     * Handler for document mouse click event
     *
     * @private
     */
    onDocumentMouseClick( event : MouseEvent ) {
        this.checkForDocumentActiveElement( event );
    }

    onDateTimeInputModeToggle() {
        const value = this.state.dateTimeInputMode === 'absolute' ? 'now' : '';

        this.setState( {
            dateTimeInputMode: this.state.dateTimeInputMode === 'absolute' ? 'relative' : 'absolute',
            value
        } );

        this.onChange( value );
    }

    onAlternativeValueFieldActiveToggle() {
        this.setState( {
            alternativeValueFieldActive: !this.state.alternativeValueFieldActive,
            value                      : ''
        } );

        this.onChange( '' );
    }

    /**
     * Handler for the filterMatchMode dropdowns change event.
     *
     * @param event
     *
     * @private
     */
    onFilterMatchModeChange( event : DropdownChangeEvent ) {
        const matchMode = event.value;

        this.onChange( undefined, matchMode );
    }

    /**
     * Handler for InputNumber FilterFields blur event.
     *
     * @param event
     *
     * @private
     */
    onInputNumberFilterBlur( event : any ) {
        if ( this.props.value === '-' ) {
            this.onFilterChange( { value: '' } );
            this.setState( { value: '' } );
        }
    }

    /**
     * Handler for the filter fields change event.
     *
     * @param event
     *
     * @private
     */
    onFilterChange( event : any ) {
        const {
                  valueType,
                  params
              } = this.props;

        let matchMode = this.props.matchMode,
            value;

        if ( matchMode === 'custom' ) {
            value = event.target.value;
        }
        else if ( params.valueSetId ) {
            value = event.value;

            // all options selected (header checkbox) -> remove empty and not empty options
            if ( value.length > 2 ) {
                if ( this.state.matchMode === 'empty' && value.indexOf( '::empty::' ) !== -1 ) {
                    value.splice( value.indexOf( '::empty::' ), 1 );
                }
                if ( this.state.matchMode === 'notEmpty' && value.indexOf( '::notEmpty::' ) !== -1 ) {
                    value.splice( value.indexOf( '::notEmpty::' ), 1 );
                }
            }

            // current matchMode empty -> remove empty option
            else if ( this.state.matchMode === 'empty' ) {
                value.splice( value.indexOf( '::empty::' ), 1 );
            }

            // current matchMode notEmpty -> remove not empty option
            else if ( this.state.matchMode === 'notEmpty' ) {
                value.splice( value.indexOf( '::notEmpty::' ), 1 );
            }

            // clear all other options for empty
            if ( value.indexOf( '::empty::' ) !== -1 ) {
                value = '::empty::';
            }

            // clear all other options for not empty
            else if ( value.indexOf( '::notEmpty::' ) !== -1 ) {
                value = '::notEmpty::';
            }

            else {
                value = value.join( '||' );
            }

            // toggle all options (header checkbox)
            if ( this.state.value === value ) {
                value = '';
            }
        }
        else {
            switch ( valueType ) {
                case 'BOOLEAN':
                    value = event.target.value;
                    if ( value === '' && ( matchMode === 'empty' || matchMode === 'notEmpty' ) ) {
                        matchMode = 'equals';
                    }
                    break;

                case 'DATE':
                    value = event;
                    if ( value !== '' && value !== undefined && value !== null && ( typeof value !== 'string' || !value.match( /^now(( (\+|\-) ([0-9]+) (seconds?|minutes?|hours?|days?|months?|years?))*)$/i ) ) ) {
                        value = moment( value ).format( 'YYYY-MM-DD' );
                    }
                    else {
                        value = null;
                    }
                    break;

                case 'CURRENCY':
                case 'DOUBLE':
                case 'DURATION':
                case 'INTEGER':
                    value = event.value;
                    if ( value !== undefined && value !== null ) {
                        value = String( value );
                    }
                    break;

                case 'STATUS':
                case 'STATUSID':
                case 'STATUSNAME':
                    value = event.value;
                    if ( value !== undefined && value !== null ) {
                        value = value.join( '||' );
                    }
                    break;

                case 'TIME':
                    value = event;
                    if ( value !== '' && value !== undefined && value !== null && ( typeof value !== 'string' || !value.match( /^now(( (\+|\-) ([0-9]+) (seconds?|minutes?|hours?|days?|months?|years?))*)$/i ) ) ) {
                        value = moment( value ).format( 'HH:mm:ss' );
                    }
                    break;

                case 'TIMESTAMP':
                    value = event;
                    if ( value !== '' && value !== undefined && value !== null && ( typeof value !== 'string' || !value.match( /^now(( (\+|\-) ([0-9]+) (seconds?|minutes?|hours?|days?|months?|years?))*)$/i ) ) ) {
                        value = moment( value ).format( 'YYYY-MM-DD HH:mm:ss' );
                    }
                    break;

                default:
                    value = event.target.value;
            }
        }

        if ( value === undefined || value === null ) {
            value = '';
        }

        this.onChange( value, matchMode !== this.props.matchMode ? matchMode : undefined );
    }

    onRelativeDateTimeFilterChange( event : any ) {
        const value = event.target.value;

        // validate
        if ( !value.match( /^now(( (\+|\-) ([0-9]+) (seconds?|minutes?|hours?|days?|months?|years?))*)$/i ) ) {
            // error
            this.setState( {
                relativeDateTimeEditValue: value
            } );
        }
        else {
            this.setState( {
                relativeDateTimeEditValue: undefined
            } );
            this.onChange( value );
        }
    }

    /**
     * Handler for changes of customFilterMode dropdown
     *
     * @param event
     *
     * @private
     */
    onCustomFilterModeChange( event : DropdownChangeEvent ) {
        const {
                  matchMode
              }                   = this.props,
              {
                  customFilters,
                  customFilterMode
              }                   = this.state,
              newCustomFilterMode = event.value;

        if ( newCustomFilterMode !== customFilterMode ) {
            let value : string | undefined = getValue( customFilters, newCustomFilterMode );

            // value empty string and equals to given one set to undefined (only change matchMode)
            if ( value === '' && value === this.state.value ) {
                value = undefined;
            }

            this.setState( {
                dontUpdateCustomFilter: true,
                customFilterMode      : newCustomFilterMode
            } );
            this.onChange( value, matchMode ?? this.getDefaultMatchMode() );
        }
    }

    /**
     * Handler for changes of the customFilterValue or customFilterMatchMode of customFilter fields
     *
     * @param {number} customFilterIndex
     * @param {string} customFilterField
     * @param {FilterMatchMode} customFilterMatchMode
     * @param {FilterValueType} customFilterValue
     *
     * @private
     */
    onCustomFilterChange( customFilterIndex : number, customFilterKey : string, customFilterField : string, customFilterMatchMode : FilterMatchMode, customFilterValue : FilterValueType ) {
        const {
                  value
              }                = this.props,
              {
                  customFilters,
                  customFilterMode
              }                = this.state,
              newCustomFilters = ( [] as CustomFilter[] ).concat( customFilters );

        newCustomFilters[ customFilterIndex ] = {
            value    : customFilterValue ?? '',
            matchMode: customFilterMatchMode
        };

        const newValue = getValue( newCustomFilters, customFilterMode );

        this.setState( {
            dontUpdateCustomFilter: newValue !== value,
            customFilters         : newCustomFilters
        } );

        if ( newValue !== value ) {
            this.onChange( newValue );
        }
    }

    /**
     * Handler for removeCustomFilter button
     *
     * @param {number} customFilterIndex
     *
     * @private
     */
    onRemoveCustomFilter( customFilterIndex : number ) {
        const {
                  matchMode
              }                = this.props,
              {
                  customFilters,
                  customFilterMode
              }                = this.state,
              newCustomFilters = ( [] as CustomFilter[] ).concat( customFilters );

        if ( newCustomFilters[ customFilterIndex ] ) {
            newCustomFilters.splice( customFilterIndex, 1 );

            this.setState( {
                dontUpdateCustomFilter: true,
                customFilters         : newCustomFilters
            } );

            this.onChange( getValue( newCustomFilters, customFilterMode ), matchMode ?? this.getDefaultMatchMode() );
        }
    }

    /**
     * Handler for addCustomFilter button
     *
     * @private
     */
    onAddCustomFilter() {
        const {
                  valueType
              }                = this.props,
              {
                  customFilters
              }                = this.state,
              newCustomFilters = ( [] as CustomFilter[] ).concat( customFilters );

        newCustomFilters.push( {
            value    : '',
            matchMode: getDefaultFilterMatchMode( valueType )
        } );

        this.setState( { customFilters: newCustomFilters } );
    }

    /**
     * Handler for changes of the filterValue or filterMatchMode
     *
     * @param {any} value
     * @param {FilterMatchMode} matchMode
     *
     * @private
     */
    onChange( value : any, matchMode? : FilterMatchMode ) {
        const {
                  alternativeValueField,
                  field,
                  fieldKey,
                  params,
                  valueType
              } = this.props,
              {
                  alternativeValueFieldActive
              } = this.state;

        if ( matchMode === undefined ) {
            matchMode = this.state.matchMode as FilterMatchMode;
        }
        if ( matchMode === undefined ) {
            matchMode = this.getDefaultMatchMode();
        }

        if ( !alternativeValueField || !alternativeValueFieldActive ) {
            if ( value === undefined || value === null ) {
                value = this.state.value;
            }

            // translate value '::empty::' to matchMode 'empty'
            if ( value === '::empty::' ) {
                value = '';
                matchMode = 'empty' as FilterMatchMode;
            }
            // translate value '::notEmpty::' to matchMode 'notEmpty'
            else if ( value === '::notEmpty::' ) {
                value = '';
                matchMode = 'notEmpty' as FilterMatchMode;
            }
            // reset matchMode for non-empty values, but only for BOOLEAN-Dropdown or ValueSet-MultiSelect
            else if ( value !== '' && ( valueType === 'BOOLEAN' || params.valueSetId ) ) {
                matchMode = 'equals' as FilterMatchMode;
            }

            // clear value for empty and not empty matching
            if ( matchMode === 'empty' || matchMode === 'notEmpty' ) {
                value = '';
            }

            // value empty string and equals to given one set to undefined (only change matchMode)
            if ( value === '' && value === this.state.value ) {
                value = undefined;
            }
        }

        this.setState( {
            value,
            matchMode
        } );

        const propOnChange = this.props.onChange;

        if ( !propOnChange ) {
            return;
        }

        const debounceMs = this.props.changeDebounceMs ?? 0;

        if ( debounceMs > 0 ) {
            if ( this.propOnChangeTimer ) {
                clearTimeout( this.propOnChangeTimer );
            }

            this.pendingPropOnChange = () => propOnChange( fieldKey, field, matchMode!, value, valueType );
            this.propOnChangeTimer = setTimeout( this.flushPendingPropOnChange, debounceMs );
        }
        else {
            propOnChange( fieldKey, field, matchMode, value, valueType );
        }
    }

    /**
     * Flushes a pending debounced {@link onChange} call immediately.
     *
     * Used before invoking {@link onEnterKey} and on unmount, so that no
     * keystrokes are lost when the user submits or navigates away.
     */
    private flushPendingPropOnChange = () : void => {
        if ( this.propOnChangeTimer ) {
            clearTimeout( this.propOnChangeTimer );
            this.propOnChangeTimer = undefined;
        }

        if ( this.pendingPropOnChange ) {
            const fn = this.pendingPropOnChange;
            this.pendingPropOnChange = undefined;
            fn();
        }
    };

    onMouseEnter() {
        const {
                  adjustWidth
              } = this.props,
              {
                  domField,
                  wrapperWidth
              } = this.state;

        if ( adjustWidth && !wrapperWidth ) {
            const wrapper               = document.getElementById( `filterField_wrapper_${domField}` ) as HTMLElement,
                  currentWrapperWidth   = wrapper.clientWidth,
                  operatorDropdown      = wrapper.querySelector( '.yn-operator-dropdown' ) as HTMLElement,
                  operatorDropdownWidth = operatorDropdown ? operatorDropdown.clientWidth : 0,
                  rem                   = parseInt( window.getComputedStyle( wrapper ).fontSize ),
                  minWrapperWidth       = ( 5 * rem ) + operatorDropdownWidth,
                  idealWrapperWidth     = ( 10 * rem ) + operatorDropdownWidth;

            if ( currentWrapperWidth < minWrapperWidth ) {
                this.setState( {
                    wrapperWidth: `${idealWrapperWidth}px`
                } );
            }
        }
    }

    onMouseLeave() {
        const {
                  isActive,
                  wrapperWidth
              } = this.state;

        if ( !isActive && wrapperWidth ) {
            this.setState( { wrapperWidth: undefined } );
        }
    }

    /**
     * Checks if any element within this field is active (has focus) and sets internal state
     *
     * @private
     */
    checkForDocumentActiveElement( event? : MouseEvent ) {
        try {
            const activeElement            = document.activeElement,
                  // eslint-disable-next-line react/no-find-dom-node
                  thisDomNode              = findDOMNode( this ) as Element | undefined,
                  matchModeDropdownDomNode = this.matchModeDropdown?.getOverlay?.(),
                  overlayPanelDomNode      = this.overlayPanel?.getElement();

            let isActive = false;

            /* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
            if (
                ( thisDomNode && ( thisDomNode.isSameNode( activeElement ) || thisDomNode.contains( activeElement ) ) )
                || ( matchModeDropdownDomNode && ( matchModeDropdownDomNode.isSameNode( activeElement ) || matchModeDropdownDomNode.contains( activeElement ) ) )
                || ( overlayPanelDomNode && ( overlayPanelDomNode.isSameNode( activeElement ) || overlayPanelDomNode.contains( activeElement ) ) )
                || ( event?.target && !this.isOutsideClicked( event ) )
            ) {
                isActive = true;
            }
            /* eslint-enable @typescript-eslint/prefer-nullish-coalescing */

            if ( isActive !== this.state.isActive ) {
                this.setState( { isActive } );

                if ( !isActive && this.state.wrapperWidth ) {
                    this.setState( { wrapperWidth: undefined } );
                }
            }
        }
        catch ( e ) {
            // do nothing
        }
    }

    /**
     * Is the (mouse~) event happened outside this component
     *
     * @param event
     */
    isOutsideClicked( event : Event ) {
        const // eslint-disable-next-line react/no-find-dom-node
            thisDomNode                     = findDOMNode( this ) as Element | undefined,
            matchModeDropdownDomNode        = this.matchModeDropdown?.getOverlay?.(),
            overlayPanelDomNode             = this.overlayPanel?.getElement(),
            customFilterModeDropdownDomNode = this.customFilterModeDropdown?.getOverlay();

        let retVal = true;

        // check custom filter fields
        if ( this.state.matchMode === 'custom' && event.target ) {
            for ( const customFilterField of this.customFilterFields ) {
                if ( customFilterField?.isOutsideClicked && !customFilterField.isOutsideClicked( event ) ) {
                    retVal = false;
                    break;
                }
            }
        }

        // check this field
        /* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
        if (
            retVal
            && event.target
            && (
                ( thisDomNode && ( thisDomNode.isSameNode( event.target as Element ) || thisDomNode.contains( event.target as Element ) ) )
                || ( matchModeDropdownDomNode && ( matchModeDropdownDomNode.isSameNode( event.target as Element ) || matchModeDropdownDomNode.contains( event.target as Element ) ) )
                || ( overlayPanelDomNode && ( overlayPanelDomNode.isSameNode( event.target as Element ) || overlayPanelDomNode.contains( event.target as Element ) ) )
                || ( customFilterModeDropdownDomNode && ( customFilterModeDropdownDomNode.isSameNode( event.target as Element ) || customFilterModeDropdownDomNode.contains( event.target as Element ) ) )
            )
        ) {
            retVal = false;
        }
        /* eslint-enable @typescript-eslint/prefer-nullish-coalescing */

        if ( retVal && this.field?.getOverlay ) {
            const overlayElement = this.field.getOverlay();

            if ( overlayElement && ( overlayElement.isSameNode( event.target as Element ) || overlayElement.contains( event.target as Element ) ) ) {
                retVal = false;
            }
        }

        return retVal;
    }

    /**
     * REACT lifecycle method
     */
    render() {
        const {
                  alternativeValueField,
                  classNames,
                  disabled,
                  markDirty,
                  markError,
                  params,
                  showMatchField,
                  showMatchModeSelector,
                  user
              }                    = this.props,
              {
                  value,
                  matchMode,
                  domField,
                  isActive,
                  wrapperWidth,
                  relativeDateTimeEditValue,
                  alternativeValueFieldActive
              }                    = this.state,
              defaultMatchMode     = this.getDefaultMatchMode(),
              filterMatchMode      = matchMode ?? defaultMatchMode,
              inputGroupClassNames = [ 'p-inputgroup', 'yn-inputgroup', 'yn-filterfield-wrapper' ].concat( classNames );

        let filterMatchModeOperatorDropdown,
            filterField,
            filterCustomOverlay;

        if ( showMatchModeSelector ) {
            filterMatchModeOperatorDropdown = this.getFilterMatchModeDropdown( filterMatchMode, disabled, user?.language ?? '', params );

            if ( !showMatchField ) {
                inputGroupClassNames.push( 'yn-filterfield-wrapper-no-field' );
            }
        }

        if ( showMatchField ) {
            if ( alternativeValueField ) {
                filterField = <div
                    className={`p-inputgroup yn-inputgroup yn-filterfield-alternative-field-wrapper`}
                    style={{ width: '100%' }}
                >
                    {alternativeValueFieldActive &&
                        alternativeValueField.getField( value, filterMatchMode, params || {}, disabled, this.onChange.bind( this ), user?.language ?? '' )
                    }
                    {!alternativeValueFieldActive &&
                        this.getFilterField( relativeDateTimeEditValue ?? value, filterMatchMode, disabled, user?.language ?? '', params, this.state.dateTimeInputMode )
                    }
                    <Button
                        icon={alternativeValueFieldActive ? alternativeValueField.icon : 'fas fa-i-cursor'}
                        style={{ flex: '0 0 auto', padding: '.5rem 0' }}
                        tooltip={alternativeValueFieldActive ? alternativeValueField.label : __( 'YN.base.filter.normalFilterInput.label' )}
                        tooltipOptions={{ showDelay: 1500 }}
                        onClick={this.onAlternativeValueFieldActiveToggle}
                        disabled={disabled}
                    />
                </div>;
            }
            else {
                filterField = this.getFilterField( relativeDateTimeEditValue ?? value, filterMatchMode, disabled, user?.language ?? '', params, this.state.dateTimeInputMode );
            }

            filterCustomOverlay = this.getFilterCustomOverlay( value, filterMatchMode, disabled, user?.language ?? '', params );

            if ( !showMatchModeSelector ) {
                inputGroupClassNames.push( 'yn-filterfield-wrapper-no-modeselector' );
            }
        }

        if ( markError ) {
            inputGroupClassNames.push( 'yn-error-border' );
        }
        else if (
            markDirty
            && (
                (
                    value !== ''
                    && value !== undefined
                    && value !== null
                )
                || (
                    filterMatchMode !== defaultMatchMode
                    && (
                        matchMode === 'empty'
                        || matchMode === 'notEmpty'
                    )
                )
            )
        ) {
            inputGroupClassNames.push( 'yn-dirty-border' );
        }

        if ( isActive ) {
            inputGroupClassNames.push( 'p-focus' );
        }

        if ( filterMatchModeOperatorDropdown ?? filterField ) {
            return <div id={`filterField_wrapper_${domField}`}
                        className={`${inputGroupClassNames.join( ' ' )} ${relativeDateTimeEditValue ? 'yn-error-border-important' : ''}`}
                        style={wrapperWidth ? { width: wrapperWidth } : undefined} onMouseEnter={this.onMouseEnter}
                        onMouseLeave={this.onMouseLeave}>
                {filterMatchModeOperatorDropdown}
                {filterField}
                {filterCustomOverlay}
            </div>;
        }
        else {
            return <></>;
        }
    }
}

/**
 * REDUX stuff
 */
function mapStateToProps( state : StoreState ) : StateProps {
    const retVal : StateProps = {
        user: undefined
    };

    // get some session user information
    if ( state.session?.user ) {
        retVal.user = state.session.user;
    }

    return retVal;
}

export default connect<StateProps, null, ComponentProps, StoreState>( mapStateToProps, null, undefined, { forwardRef: true } )( FilterField );
